<template>
  <div class="article-tags-filter">
    <div class="tags-title">Теги</div>
    <SvoiSelect
      :model-value="selectValues"
      :options="selectOptions"
      item-label="name"
      label="Выберите теги"
      multiple
      class="tags-select"
      @update:model-value="onSelectChange"
    />
    <div v-if="popularTags.length" class="tags-popular">
      <BaseTag
        v-for="tag in popularTags"
        :key="tag.id"
        :class="{ '-active': selectedIds.has(tag.id) }"
        :title="tag.name"
        @click="togglePopular(tag)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { OptionSelect } from '@svoi-ui/interfaces/select'
  import { SvoiSelect } from '@svoi-ui/components/select'
  import { Tag as BaseTag } from '~/components/base-ui/tags'
  import type { ArticleFilterOption } from '~/models/articles'

  type ArticleFilterSelectOption = ArticleFilterOption & OptionSelect

  const props = defineProps<{
    tags: ArticleFilterOption[]
    popularTags: ArticleFilterOption[]
    initialTagIds?: string
  }>()

  const emit = defineEmits<{
    (e: 'on-change', value: { tags_id?: string }): void
  }>()

  const selectedIds = ref(new Set<number>())

  function syncFromProps() {
    if (props.initialTagIds) {
      selectedIds.value = new Set(props.initialTagIds.split(',').map(Number).filter(Boolean))
    } else {
      selectedIds.value = new Set()
    }
  }

  watch(() => props.initialTagIds, syncFromProps, { immediate: true })

  const selectOptions = computed(() => props.tags as ArticleFilterSelectOption[])
  const selectValues = computed(() => selectOptions.value.filter(o => selectedIds.value.has(o.id)))

  function emitIds() {
    const ids = [...selectedIds.value]
    emit('on-change', { tags_id: ids.length ? ids.join(',') : undefined })
  }

  function onSelectChange(value: OptionSelect[] | OptionSelect | null) {
    const values = Array.isArray(value) ? (value as ArticleFilterSelectOption[]) : []
    const selectedFromSelect = new Set(values.map(v => v.id))
    const popularIds = new Set(props.popularTags.map(t => t.id))

    const result = new Set<number>()
    selectedFromSelect.forEach(id => result.add(id))
    selectedIds.value.forEach(id => {
      if (popularIds.has(id)) result.add(id)
    })

    selectedIds.value = result
    emitIds()
  }

  function togglePopular(tag: ArticleFilterOption) {
    const next = new Set(selectedIds.value)
    if (next.has(tag.id)) next.delete(tag.id)
    else next.add(tag.id)
    selectedIds.value = next
    emitIds()
  }
</script>

<style lang="scss" scoped>
  .article-tags-filter {
    @extend %flex-column;

    gap: 12px;

    > .tags-title {
      font-size: 20px;
      font-weight: 500;
      margin-bottom: 8px;

      @include vp-767 {
        font-size: 16px;
      }
    }

    > .tags-select {
      margin-bottom: 8px;
    }

    > .tags-popular {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      > :deep(.tag) {
        cursor: pointer;
        transition:
          background 0.15s,
          color 0.15s;

        &.-active {
          background: $color-kiwi-50;
          color: $color-default-white;
        }
      }
    }

    .svoi-select:deep(.multiselect__tags-wrap .multiselect__tag) {
      background: $color-kiwi-50;
      color: $color-default-white;
    }
  }
</style>
